<script setup lang="ts" generic="TData">
  import type { BaseActionDropdownItem } from '@gat-ship/types'
  import { type HTMLAttributes, computed, ref } from 'vue'
  import { cn } from '../../lib/utils'
  import { type GatButtonProps, GatButton } from '../gat-button'
  import { GatDropdownMenu } from '../gat-dropdown-menu'

  export interface DataTableAction {
    button?: GatButtonProps & {
      /**
       * The visibility state of the button.
       * @param hover - The button will be visible on hover (mouseover).
       * @param always - The button will always be visible.
       * @default 'hover'
       */
      showOn?: 'hover' | 'always'
    }
    items?: BaseActionDropdownItem[]
  }

  export interface DataTableActionsProps<TData> {
    class?: HTMLAttributes['class']
    /** The size of the actions column. This size will be absolute, and will not shrink/grow. */
    size?: number
    /** The "Main Action" button to display for the row. */
    buttons?: (row: TData) => DataTableAction[]
  }

  const props = defineProps<DataTableActionsProps<TData> & { row: TData }>()

  const showButton = ref<Record<string, boolean>>({})

  const _buttons = computed(() => {
    if (props.buttons && typeof props.buttons === 'function') {
      return props.buttons(props.row)
    }
    return []
  })

  function onTriggerClicked(button: GatButtonProps | undefined) {
    if (!button) {
      return
    }
    if (button.onClick) {
      if (Array.isArray(button.onClick)) {
        button.onClick.forEach((e) => e())
      } else {
        button.onClick()
      }
    }
  }

  defineOptions({ inheritAttrs: false })
</script>

<template>
  <div class="ml-auto flex grow items-center gap-2">
    <template v-for="(button, index) in _buttons" :key="index">
      <div
        :data-open="showButton[index]"
        :class="
          cn(
            `${button.button?.showOn === 'hover' ? 'opacity-0 group-hover/row:opacity-100 data-[open=true]:opacity-100' : ''}`,
            'w-fit'
          )
        "
      >
        <gat-dropdown-menu
          :items="button.items"
          v-model:open="showButton[index]"
        >
          <template #trigger>
            <GatButton
              v-bind="button.button"
              :variant="button.button?.variant ?? 'ghost'"
              :label="button.button?.label ?? button.button?.text"
              :size="button.button?.size ?? 'icon'"
              :icon="
                button.button?.icon !== false
                  ? (button.button?.icon ?? 'EllipsisVertical')
                  : false
              "
              :test-id="button.button?.testId ?? button.button?.dataTestid"
              :disabled="button.button?.disabled"
              :tooltip="button.button?.tooltip"
              :class="button.button?.class"
              @click.stop="onTriggerClicked(button.button)"
            >
            </GatButton>
          </template>
        </gat-dropdown-menu>
      </div>
    </template>
  </div>
</template>
